import { computed, unref, type ComputedRef, type MaybeRef } from 'vue';

import i18n from '@/i18n';

const t = i18n.global.t;

type WithChildren<T> = T & { children?: T[] };

const getChildren = <T extends object>(item: T): T[] | undefined => {
	if (!('children' in item)) {
		return undefined;
	}
	const children = item['children' as keyof T];
	return Array.isArray(children) ? children as T[] : undefined;
};

const addTranslatedField = <
		T extends object,
		S extends keyof T & string,
		K extends string,
	>(
		items: MaybeRef<readonly T[]>,
		sourceField: S,
		targetField: K,
		prefix: string,
	): ComputedRef<(T & Partial<Record<K, string>>)[]> => {
	type Result = T & Partial<Record<K, string>>;

	const translateOne = (item: T): Result => {
		const sourceValue = item[sourceField];
		const base = { ...item };
		if (sourceValue != null) {
			return Object.assign(base, {
				[targetField]: i18n.global.t(prefix + '.' + String(sourceValue)),
			});
		}
		return base;
	};

	return computed(() => unref(items).map(item => {
		const result: WithChildren<Result> = translateOne(item);
		const children = getChildren(item);
		if (children != null) {
			result.children = children.map(translateOne);
		}
		return result;
	}));
};

export default {
	t,
	addTranslatedField,
};
